import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import analyticsService from '../lib/AnalyticsService';

// Hook for tracking page views
export const usePageTracking = () => {
  const location = useLocation();
  const previousLocation = useRef();

  useEffect(() => {
    const currentPath = location.pathname + location.search;
    const previousPath = previousLocation.current;

    // Only track if location actually changed
    if (previousPath !== currentPath) {
      // Extract step name from pathname
      const stepMatch = location.pathname.match(/\/onboarding\/(.+)$/);
      const stepName = stepMatch ? stepMatch[1] : location.pathname;

      // Track page view with analytics service
      analyticsService.trackPageView(currentPath, {
        step_name: stepName,
        previous_page: previousPath,
        is_onboarding: location.pathname.includes('/onboarding/'),
        query_params: Object.fromEntries(new URLSearchParams(location.search))
      });
      
      previousLocation.current = currentPath;
    }
  }, [location]);
};

// Hook for tracking user interactions
export const useInteractionTracking = () => {
  const trackClick = useCallback((element, additionalProps = {}) => {
    analyticsService.trackInteraction('click', element, additionalProps);
  }, []);

  const trackFormSubmit = useCallback((formName, formData, success = true) => {
    analyticsService.trackFormSubmit(formName, formData, success);
  }, []);

  const trackButtonClick = useCallback((buttonName, additionalProps = {}) => {
    analyticsService.trackEvent('Insurance Profile Button Click', {
      button_name: buttonName,
      ...additionalProps
    });
  }, []);

  return {
    trackClick,
    trackFormSubmit,
    trackButtonClick,
  };
};

// Hook for tracking errors
export const useErrorTracking = () => {
  const trackError = useCallback((errorName, error, context = {}) => {
    analyticsService.trackError(errorName, error, context);
  }, []);

  const trackAPIError = useCallback((endpoint, error, context = {}) => {
    analyticsService.trackError('api_error', error, {
      endpoint,
      ...context
    });
  }, []);

  const trackNetworkError = useCallback((error, context = {}) => {
    analyticsService.trackError('network_error', error, context);
  }, []);

  return {
    trackError,
    trackAPIError,
    trackNetworkError
  };
};

// Hook for tracking authentication events
export const useAuthTracking = () => {
  const trackLogin = useCallback((method, success = true, error = null) => {
    analyticsService.trackAuth('login', method, success, error);
  }, []);

  const trackLogout = useCallback((method = 'manual') => {
    analyticsService.trackAuth('logout', method, true);
    analyticsService.resetUser();
  }, []);

  const trackSignup = useCallback((method, success = true, error = null) => {
    analyticsService.trackAuth('signup', method, success, error);
  }, []);

  const trackVerification = useCallback((method, success = true, error = null) => {
    analyticsService.trackAuth('verification', method, success, error);
  }, []);

  return {
    trackLogin,
    trackLogout,
    trackSignup,
    trackVerification
  };
};

// Hook for tracking file operations
export const useFileTracking = () => {
  const trackFileUpload = useCallback((fileName, fileSize, fileType, success = true, error = null) => {
    analyticsService.trackFileUpload(fileName, fileSize, fileType, success, error);
  }, []);

  const trackDocumentView = useCallback((documentName, documentType) => {
    analyticsService.trackEvent('Document View', {
      document_name: documentName,
      document_type: documentType,
      timestamp: new Date().toISOString()
    });
  }, []);

  return {
    trackFileUpload,
    trackDocumentView
  };
};

// Hook for tracking survey and form interactions
export const useSurveyTracking = () => {
  const trackSurveyStart = useCallback((surveyName) => {
    analyticsService.trackEvent('Survey Start', {
      survey_name: surveyName,
      start_timestamp: new Date().toISOString()
    });
  }, []);

  const trackSurveyQuestion = useCallback((surveyName, questionNumber, questionText, answer) => {
    analyticsService.trackEvent('Survey Question', {
      survey_name: surveyName,
      question_number: questionNumber,
      question_text: questionText?.slice(0, 100), // Limit length
      answer_type: typeof answer,
      has_answer: answer !== null && answer !== undefined && answer !== '',
      timestamp: new Date().toISOString()
    });
  }, []);

  const trackSurveyComplete = useCallback((surveyName, totalQuestions, completionTime) => {
    analyticsService.trackEvent('Survey Complete', {
      survey_name: surveyName,
      total_questions: totalQuestions,
      completion_time_ms: completionTime,
      timestamp: new Date().toISOString()
    });
  }, []);

  const trackSurveyAbandonment = useCallback((surveyName, questionNumber, totalQuestions) => {
    analyticsService.trackEvent('Survey Abandoned', {
      survey_name: surveyName,
      abandoned_at_question: questionNumber,
      total_questions: totalQuestions,
      completion_percentage: (questionNumber / totalQuestions) * 100,
      timestamp: new Date().toISOString()
    });
  }, []);

  return {
    trackSurveyStart,
    trackSurveyQuestion,
    trackSurveyComplete,
    trackSurveyAbandonment
  };
};

// Hook for tracking integrations (DIMO, Canopy, etc.)
export const useIntegrationTracking = () => {
  const trackIntegrationStart = useCallback((integrationType, integrationName) => {
    analyticsService.trackEvent('Integration Start', {
      integration_type: integrationType,
      integration_name: integrationName,
      start_timestamp: new Date().toISOString()
    });
  }, []);

  const trackIntegrationSuccess = useCallback((integrationType, integrationName, data = {}) => {
    analyticsService.trackEvent('Integration Success', {
      integration_type: integrationType,
      integration_name: integrationName,
      success_timestamp: new Date().toISOString(),
      ...data
    });
  }, []);

  const trackIntegrationError = useCallback((integrationType, integrationName, error) => {
    analyticsService.trackError('integration_error', error, {
      integration_type: integrationType,
      integration_name: integrationName
    });
  }, []);

  const trackIntegrationSkip = useCallback((integrationType, integrationName, reason = 'user_choice') => {
    analyticsService.trackEvent('Integration Skipped', {
      integration_type: integrationType,
      integration_name: integrationName,
      skip_reason: reason,
      timestamp: new Date().toISOString()
    });
  }, []);

  return {
    trackIntegrationStart,
    trackIntegrationSuccess,
    trackIntegrationError,
    trackIntegrationSkip
  };
};
